import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useConvexMutation } from './use-convex-mutation';

// ════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════

export interface ParentStats {
  totalChildren: number;
  avgAttendance: number;
  recentEvaluations: number;
  upcomingSessions: number;
}

export interface ChildSummary {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  level: string | null;
  utr: string | null;
  relationshipType: string | null;
  attendancePercent: number;
  lastEvalAvg: number | null;
  lastEvalDate: string | null;
}

export interface Evaluation {
  id: string;
  attitude: number;
  effort: number;
  engagement: number;
  physical: number | null;
  technical: number | null;
  tactical: number | null;
  competitionLevel: number;
  notes: string | null;
  createdAt: number;
  coachId?: string;
  average?: number;
}

export interface AttendanceRecord {
  id: string;
  status: 'present' | 'late' | 'absent' | 'excused';
  sessionDate: string;
  sessionType: string | null;
  courtNumber: number | null;
  startTime: string;
  endTime: string;
  drillLog: string[];
}

export interface UpcomingSession {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  sessionType: string | null;
  courtNumber: number | null;
}

export interface ChildDetail {
  profile: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
    level: string | null;
    utr: number | null;
    ballLevel: string | null;
    points: number;
    relationshipType: string | null;
  };
  recentSessionNote: string | null;
  evaluations: Evaluation[];
  attendance: AttendanceRecord[];
  upcomingSessions: UpcomingSession[];
  stats: {
    attendancePercent: number;
    attendanceStats: {
      total: number;
      present: number;
      late: number;
      absent: number;
    };
    evalAverages: {
      attitude: number;
      effort: number;
      engagement: number;
      physical: number | null;
      technical: number | null;
      tactical: number | null;
    } | null;
    totalEvaluations: number;
  };
}

export interface PlayerGearProfile {
  id: string;
  playerId: string;
  racquetBrand: string | null;
  racquetModel: string | null;
  gripSize: string | null;
  stringBrand: string | null;
  stringType: string | null;
  stringTension: string | null;
  shoeBrand: string | null;
  shoeModel: string | null;
  shoeSize: string | null;
  playingStyle: string | null;
  dominantHand: string | null;
  backhandType: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface UpdateGearProfileData {
  racquetBrand?: string | null;
  racquetModel?: string | null;
  gripSize?: string | null;
  stringBrand?: string | null;
  stringType?: string | null;
  stringTension?: string | null;
  shoeBrand?: string | null;
  shoeModel?: string | null;
  shoeSize?: string | null;
  playingStyle?: string | null;
  dominantHand?: string | null;
  backhandType?: string | null;
}

// ════════════════════════════════════════════════════════════
// QUERY HOOKS
// ════════════════════════════════════════════════════════════

export function useParentStats() {
  const data = useQuery(api.parent.stats.get);
  return { data, isLoading: data === undefined, error: null };
}

export function useParentChildren() {
  const data = useQuery(api.parent.children.list);
  return { data, isLoading: data === undefined, error: null };
}

export function useChildDetail(childId: string) {
  const data = useQuery(
    api.parent.children.getDetail,
    childId ? { childId: childId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

export function usePlayerGearProfile(childId: string) {
  const data = useQuery(
    api.parent.children.getGearProfile,
    childId ? { childId: childId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

export function useParentSessionsForMonth(year: number, month: number) {
  const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const data = useQuery(api.parent.sessions.listByDateRange, { startDate, endDate });
  return { data, isLoading: data === undefined, error: null };
}

// ════════════════════════════════════════════════════════════
// FITNESS HOOKS
// ════════════════════════════════════════════════════════════

export function useChildFitnessHistory(childId: string, testType?: string) {
  const data = useQuery(
    api.parent.fitnessResults.getHistoryForChild,
    childId ? { childId: childId as Id<"players">, testType } : 'skip'
  );
  return { data: data ?? [], isLoading: data === undefined };
}

export function useChildClubBenchmarks(childId: string) {
  const data = useQuery(
    api.parent.fitnessResults.getClubBenchmarks,
    childId ? { childId: childId as Id<"players"> } : 'skip'
  );
  return { data: data ?? [], isLoading: data === undefined };
}

export function useChildAgeMatchedBenchmarks(childId: string) {
  const data = useQuery(
    api.parent.fitnessResults.getAgeMatchedBenchmarks,
    childId ? { childId: childId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined };
}

// ════════════════════════════════════════════════════════════
// MUTATION HOOKS
// ════════════════════════════════════════════════════════════

export function useUpdateGearProfile(childId: string) {
  const { mutateAsync, isPending } = useConvexMutation(api.parent.children.updateGearProfile);
  return {
    isPending,
    mutateAsync: (data: UpdateGearProfileData) => mutateAsync({
      childId: childId as Id<"players">,
      racquetBrand: data.racquetBrand ?? undefined,
      racquetModel: data.racquetModel ?? undefined,
      gripSize: data.gripSize ?? undefined,
      stringBrand: data.stringBrand ?? undefined,
      stringType: data.stringType ?? undefined,
      stringTension: data.stringTension ?? undefined,
      shoeBrand: data.shoeBrand ?? undefined,
      shoeModel: data.shoeModel ?? undefined,
      shoeSize: data.shoeSize ?? undefined,
      playingStyle: data.playingStyle ?? undefined,
      dominantHand: data.dominantHand ?? undefined,
      backhandType: data.backhandType ?? undefined,
    }),
  };
}
